import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'

interface VersionInfo {
  version: string
  buildDate: string
  commitSha: string
  /**
   * NODE_ENV, which is 'production' on DEV as well. Kept because three places already
   * render it; use `envName` for anything that needs to tell dev from prod.
   */
  environment: string
  /** Cloud Run K_SERVICE resolved to 'local' | 'dev' | 'prod' | a service name. */
  envName?: string
}

/**
 * Hook to fetch application version information
 * Caches the result since version doesn't change during a session
 */
export function useVersion() {
  return useQuery<VersionInfo>({
    queryKey: ['version'],
    queryFn: async () => {
      const response = await api.get('/version')
      return response.data
    },
    staleTime: Infinity, // Version never goes stale during a session
    gcTime: Infinity, // Keep in cache forever
    // RETRIED, because this answer decides whether the environment badge appears.
    //
    // It used to be `retry: false`, which was harmless while this only fed a version
    // string. It is not harmless now: with staleTime Infinity, ONE transient failure at
    // app load left dev with no badge and an unprefixed tab for the whole session —
    // byte-identical to production, which is the single mistake this indicator exists to
    // prevent. Measured: a failing query produced an empty badge and the exact production
    // title.
    retry: 2,
    refetchOnWindowFocus: false, // Don't refetch on window focus
  })
}

export type { VersionInfo }
